/**
 * Страница «Аналитика» (redesign по макету): ряд фильтров-чипов по разделам
 * (Население · Экономика · …) + секции с виджетами.
 *
 * Свёрстаны разделы «Все» (сводная витрина US-04.1, открывается по
 * умолчанию), «Население», «Экономика», «Социальная сфера», «Сельское
 * хозяйство», «Земельный фонд», «Инфраструктура», «Строительство»,
 * «Потребности СНП» (облегчённая версия US-04.9.1) — каждый показывается
 * на своём чипе; моки из hardData для демо-КАТО. «Торговля» (US-30) —
 * новый подраздел без моков: плашка и блоки берут данные только с бэка.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useKato } from '../../contexts/KatoContext';

import { useAnalyticsSectionPresent } from './analytics-visibility';

import { AgricultureSection } from './components/AgricultureSection';
import { AllSection } from './components/AllSection';
import { ConstructionSection } from './components/ConstructionSection';
import { EconomySection } from './components/EconomySection';
import { InfrastructureSection } from './components/InfrastructureSection';
import { LandFundSection } from './components/LandFundSection';
import { PopulationSection } from './components/PopulationSection';
import { SnpNeedsSection } from './components/SnpNeedsSection';
import { SocialSection } from './components/SocialSection';
import { TradeSection } from './components/TradeSection';
import { cn } from '../../lib/cn';

export type AnalyticsFilter =
  | 'all'
  | 'population'
  | 'economy'
  | 'social'
  | 'agriculture'
  | 'landFund'
  | 'infrastructure'
  | 'construction'
  | 'trade'
  | 'snpNeeds';

/** «Все» — первый и по умолчанию (US-04.1 FR-1.1); «Инфраструктура» —
 * за «Земельным фондом», «Строительство» — за ней (US-04.7/04.8 FR-1.1).
 * «Торговля» (US-30 FR-1.1) стоит перед «Потребностями СНП»: её место в
 * ряду постановкой не задано (US-30, открытый вопрос 6) и определено
 * пользователем — «Потребности СНП» замыкают ряд. */
const FILTERS: AnalyticsFilter[] = [
  'all',
  'population',
  'economy',
  'social',
  'agriculture',
  'landFund',
  'infrastructure',
  'construction',
  'trade',
  'snpNeeds',
];

/** Разделы, у которых уже есть вёрстка; остальные чипы некликабельны. */
const ACTIVE_FILTERS: AnalyticsFilter[] = [
  'all',
  'population',
  'economy',
  'social',
  'agriculture',
  'landFund',
  'infrastructure',
  'construction',
  'trade',
  'snpNeeds',
];

/** Раздел из query (?section=…) — для восстановления по кнопке «назад»
 * после drill-down в село («Потребности СНП») и для деп-линков. */
const sectionFromUrl = (): AnalyticsFilter | null => {
  const section = new URLSearchParams(window.location.search).get('section');
  return section !== null && (FILTERS as string[]).includes(section)
    ? (section as AnalyticsFilter)
    : null;
};

export const AnalyticsPage = () => {
  const { t } = useTranslation();
  const { setSelectedKato } = useKato();
  const [filter, setFilter] = useState<AnalyticsFilter>(
    () => sectionFromUrl() ?? 'all',
  );

  // Видимость разделов управляется супер-админом (US-09 FR-1): скрытый
  // раздел исчезает из чипов и не рендерится (в сводной «Все» — тоже,
  // см. AllSection).
  const sectionPresent = useAnalyticsSectionPresent();
  const visibleFilters = FILTERS.filter(sectionPresent);

  // Текущий раздел скрыли (или деп-линк на скрытый) — уходим на «Все».
  const filterHidden = !sectionPresent(filter);
  useEffect(() => {
    if (filterHidden) setFilter('all');
  }, [filterHidden]);

  // Кнопки «назад»/«вперёд»: восстановить раздел и КАТО из query записи
  // истории (точку возврата пишет drill-down в село, SnpVillages).
  useEffect(() => {
    const restore = () => {
      const section = sectionFromUrl();
      if (section !== null) {
        setFilter(section);
      }
      const kato = Number.parseInt(
        new URLSearchParams(window.location.search).get('kato') ?? '',
        10,
      );
      if (Number.isFinite(kato)) {
        setSelectedKato(kato);
      }
    };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, [setSelectedKato]);

  return (
    <div className="space-y-4 p-5">
      {/* Одна строка всегда: на узких экранах — горизонтальная прокрутка. */}
      <div
        className="flex items-center gap-2.5 overflow-x-auto pb-1"
        data-testid="analytics-filters"
      >
        {visibleFilters.map((key) => {
          const isDisabled = !ACTIVE_FILTERS.includes(key);
          return (
            <button
              key={key}
              type="button"
              disabled={isDisabled}
              aria-disabled={isDisabled}
              title={isDisabled ? t('menu.inDevelopment') : undefined}
              aria-pressed={filter === key}
              onClick={() => {
                setFilter(key);
                // Раздел — в query текущей записи (для «назад» и деп-линков).
                window.history.replaceState(
                  window.history.state,
                  '',
                  `?section=${key}`,
                );
              }}
              data-testid={`analytics-filter-${key}`}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-xl px-2 py-1.5 text-sm font-medium transition-colors',
                filter === key
                  ? 'bg-text text-text-inverted'
                  : isDisabled
                    ? 'cursor-not-allowed bg-surface-muted text-text-faint'
                    : 'bg-surface-muted text-text hover:bg-surface-border',
              )}
            >
              {t(`analytics.filters.${key}`)}
            </button>
          );
        })}
      </div>

      {filter === 'all' && <AllSection onNavigate={setFilter} />}
      {filter === 'population' && sectionPresent(filter) && <PopulationSection />}
      {filter === 'economy' && sectionPresent(filter) && <EconomySection />}
      {filter === 'social' && sectionPresent(filter) && <SocialSection />}
      {filter === 'agriculture' && sectionPresent(filter) && <AgricultureSection />}
      {filter === 'landFund' && sectionPresent(filter) && <LandFundSection />}
      {filter === 'infrastructure' && sectionPresent(filter) && (
        <InfrastructureSection />
      )}
      {filter === 'construction' && sectionPresent(filter) && (
        <ConstructionSection />
      )}
      {filter === 'snpNeeds' && sectionPresent(filter) && <SnpNeedsSection />}
      {filter === 'trade' && sectionPresent(filter) && <TradeSection />}
    </div>
  );
};
